/**
 * <TutorStudentChips> — la puce d'un élève du live, posée sur sa vidéo (molecule).
 *
 * Weekly du 03/11 (W9) : la vidéo ne porte plus que **son chrono**, jamais masqué ni remplacé par un
 * état (le temps d'échange en Dialogue, le temps depuis le dernier échange en Concentré : gris, orange à
 * 5 min, rouge à 10). Seule sa présence passe avant (hors-ligne, pas encore là), parce qu'alors il n'y a
 * pas de chrono. Ce qui demande le masteur (main levée, exercice rendu, hors appli, silence, une
 * décision, un message) passe par la pastille rouge de sa carte (`tutorStudentAttention`) et se lit
 * dans le fil.
 *
 * La chip du livre (`<Badge>`) ; les mots sont ceux des anciennes infobulles (`LiveParticipantIndicator`),
 * et `tutorStudentFaces` les tient.
 */
import React, { useRef } from "react"
import { View } from "react-native"

import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Badge } from "../../atoms/Badge"
import { useSecondClock } from "../../hooks/useSecondClock"
import {
    CHRONO_SECONDS_BELOW,
    tutorStudentChronoChip,
    tutorStudentChronoHeld,
    tutorStudentChronoLabel,
    tutorStudentStateChip,
} from "./tutorStudentFaces"

export type TutorStudentChipsProps = {
    student: UITutorLiveStudent
    testID?: string
}

export function TutorStudentChips(props: TutorStudentChipsProps) {
    const { student, testID } = props
    const absent = student.presence === "here" ? null : tutorStudentStateChip(student)
    if (absent != null) return <Badge {...absent} truncate={false} testID={testID} />
    return <ChronoChip student={student} testID={testID} />
}

/**
 * Le chrono, seul à avancer à la seconde (weekly du 03/11, W9) : sous 10 min, il dit ses secondes et relit
 * l'heure ; au-delà, ses minutes, et le relevé de la salle suffit. Rien d'autre de la carte ne se redessine.
 */
function ChronoChip(props: { student: UITutorLiveStudent; testID?: string }) {
    const { student, testID } = props
    const { kind, seconds: counted, runningSince } = student.chrono
    const since = runningSince != null && counted < CHRONO_SECONDS_BELOW ? runningSince : null
    const now = useSecondClock(since)
    const shown = useRef<{ id: string; kind: string; seconds: number } | null>(null)
    const live = since != null ? Math.max(counted, Math.floor((now - since) / 1000)) : counted
    const seconds = tutorStudentChronoHeld(shown.current, student.id, kind, live)
    shown.current = { id: student.id, kind, seconds }
    const chrono = tutorStudentChronoChip(student, seconds)
    return (
        <View accessible accessibilityLabel={`${tutorStudentChronoLabel(student)} : ${chrono.text}`} testID={testID}>
            <Badge {...chrono} truncate={false} />
        </View>
    )
}
